// Panel de detalle de la promo de sistema del bono de bienvenida: solo se edita el monto y solo se
// pausa o reanuda.
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
    LifecycleActions,
    LifecycleEditorShell,
    LifecycleNotice,
    LifecycleEditorLayout,
} from '@/shared/patterns/Lifecycle';
import { StatusPill } from '@/shared/patterns/Pills';
import type { SalePromotionListItem, SalePromotionTransition } from '../api/promotionsContract';
import {
    SYSTEM_PROMOTION_BADGE,
    SYSTEM_PROMOTION_BADGE_TITLE,
    welcomeBonusFormSchema,
    welcomeBonusFormValues,
    type WelcomeBonusFormValues,
} from '../lib/systemPromotion';
import { listStatusOf } from '../lib/promotionsList';
import { AmountInput, EditorField } from '@/shared/patterns/EditorForm';
import { WELCOME_BONUS_HELP } from '../constants/helpText';
import { promotionLifecycleActions } from './promotionLifecycle';
import { PromotionLogPanel } from './PromotionLogPanel';

export interface WelcomeBonusEditorProps {
    promotion: SalePromotionListItem;
    now: Date;
    editable: boolean;
    canReadLog: boolean;
    saving: boolean;
    /** Resuelve `true` si guardó; entonces lo tecleado pasa a ser lo guardado. */
    onSave: (values: WelcomeBonusFormValues) => Promise<boolean>;
    onDirtyChange: (dirty: boolean) => void;
    onAction: (transition: SalePromotionTransition) => void;
}

export function WelcomeBonusEditor({
    promotion,
    now,
    editable,
    canReadLog,
    saving,
    onSave,
    onDirtyChange,
    onAction,
}: WelcomeBonusEditorProps) {
    const defaultValues = welcomeBonusFormValues(promotion);
    const {
        register,
        reset,
        handleSubmit,
        formState: { errors, isDirty },
    } = useForm<WelcomeBonusFormValues>({
        defaultValues,
        resolver: zodResolver(welcomeBonusFormSchema),
    });
    const showSave = editable && isDirty;

    useEffect(() => {
        onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    const submit = handleSubmit(async (values) => {
        if (await onSave(values)) reset(values);
    });

    return (
        <LifecycleEditorShell
            ariaLabel={`Promo ${promotion.name}`}
            title={promotion.name}
            dirty={isDirty}
            subtitle={`Versión ${String(promotion.version)} · La crea el sistema para cada cadena`}
            status={listStatusOf(promotion, now)}
            id={{ label: 'ID', value: promotion.promotionId }}
            tags={
                <StatusPill tone="info" title={SYSTEM_PROMOTION_BADGE_TITLE}>
                    {SYSTEM_PROMOTION_BADGE}
                </StatusPill>
            }
            actions={
                <LifecycleActions
                    actions={promotionLifecycleActions(promotion, now, onAction)}
                    dirty={isDirty}
                    menuLabel="Acciones de la promo"
                />
            }
            notices={
                <LifecycleNotice role="note">
                    Se ofrece al abrir la cuenta del cliente. Solo se cambia el monto, o se pausa
                    para dejar de ofrecerlo; no se publica, se vence ni se archiva.
                </LifecycleNotice>
            }
            history={
                canReadLog ? (
                    <PromotionLogPanel
                        promotionId={promotion.promotionId}
                        retailerId={promotion.retailerId}
                        compact
                    />
                ) : null
            }
            saveBar={{
                count: showSave ? 1 : 0,
                message: 'Cambios sin guardar',
                saving,
                onDiscard: () => {
                    reset(defaultValues);
                },
                onSave: () => {
                    void submit();
                },
            }}
        >
            <fieldset disabled={!editable} className="min-w-0">
                <LifecycleEditorLayout
                    levers={
                        <>
                            <EditorField
                                label="Monto del bono"
                                help={WELCOME_BONUS_HELP.amount}
                                error={errors.amount?.message}
                            >
                                {(id) => (
                                    <AmountInput
                                        id={id}
                                        type="text"
                                        placeholder="0"
                                        unit="$ MXN"
                                        aria-invalid={errors.amount ? true : undefined}
                                        {...register('amount')}
                                    />
                                )}
                            </EditorField>
                            <p className="mt-2 text-[10.5px] text-muted-foreground">
                                Se deposita en la cuenta PagoConFiado del cliente. Guardar crea la
                                versión {promotion.version + 1}; las cuentas ya abiertas conservan
                                su bono.
                            </p>
                        </>
                    }
                />
            </fieldset>
        </LifecycleEditorShell>
    );
}
